// src/lib/fabric/client.ts

import { RealtimeEventHandler } from './event_handler.js';
import { FabricAPI } from './api.js';
import { FabricConversation } from './conversation.js';
import { RealtimeEvent } from '../utils/events';

declare module './api' {
    interface FabricAPI {
        fetch(endpoint: string, method?: string, data?: any | FormData, timeout?: number, isBlob?: boolean): Promise<any>;
    }
}

interface SessionConfig {
    instructions?: string;
    tools?: any[];
    tool_choice?: string;
    temperature?: number;
    model?: string;
    max_response_output_tokens?: number;
    seed_id?: string;
    instance_id?: string;
    should_modify?: boolean;
    use_system_two?: boolean;
}

interface FabricClientConfig {
    url?: string;
    apiKey?: string;
    dangerouslyAllowAPIKeyInBrowser?: boolean;
    debug?: boolean;
}

export class FabricClient extends RealtimeEventHandler {
    private defaultSessionConfig: SessionConfig;
    private sessionConfig: SessionConfig;
    private api: FabricAPI;
    private conversation: FabricConversation;
    private sessionCreated: boolean;
    private tools: Record<string, any>;

    constructor(config: FabricClientConfig = {}) {
        super(); // Ensure RealtimeEventHandler is properly initialized

        this.defaultSessionConfig = {
            instructions: '',
            tools: [],
            tool_choice: 'auto',
            temperature: 0,
            model: '',
            max_response_output_tokens: 4096,
        };

        this.sessionConfig = {};
        this.api = new FabricAPI({
            url: config.url,
            apiKey: config.apiKey,
            dangerouslyAllowAPIKeyInBrowser: config.dangerouslyAllowAPIKeyInBrowser,
            debug: config.debug
        });
        this.conversation = new FabricConversation();
        this.sessionCreated = false;
        this.tools = {};

        this._resetConfig();
        this._addAPIEventHandlers();
    }

    private _resetConfig(): boolean {
        this.sessionCreated = false;
        this.tools = {};
        this.sessionConfig = JSON.parse(JSON.stringify(this.defaultSessionConfig));
        return true;
    }

    private _addAPIEventHandlers(): void {
        // Event Logging handlers
        this.api.on('client.*', (event: RealtimeEvent) => {
            const fabricEvent: RealtimeEvent = {
                time: new Date().toISOString(),
                source: 'client',
                isFabric: true,
                event: event,
            };
            this.dispatch('fabric.event', fabricEvent);
        });

        this.api.on('server.*', (event: RealtimeEvent) => {
            const fabricEvent: RealtimeEvent = {
                time: new Date().toISOString(),
                source: 'server',
                isFabric: true,
                event: event,
            };
            this.dispatch('fabric.event', fabricEvent);
        });

        // Handle conversation updates
        const handler = (event: any, ...args: any[]) => {
            const { item, delta } = this.conversation.processEvent(event, ...args);
            if (item) {
                this.dispatch('conversation.updated', { item, delta });
            }
            return { item, delta };
        };

        // Register all fabric event handlers
        this.api.on('server.fabric.conversation.created', handler);
        this.api.on('server.fabric.conversation.item.created', handler);
        this.api.on('server.fabric.conversation.item.deleted', handler);
        this.api.on('server.fabric.response.created', handler);
        this.api.on('server.fabric.response.output_item.added', handler);
        this.api.on('server.fabric.response.output_item.done', (event: RealtimeEvent) => {
            const { item } = handler(event);
            if (item) {
                this.dispatch('conversation.item.completed', { item });
            }
        });
        this.api.on('server.fabric.response.done', handler);
    }
    async connect(endpoint = 'fabric', completion = () => { return true }): Promise<boolean> {
        if (this.isConnected()) {
            throw new Error('Already connected, use .disconnect() first');
        }
        await this.api.connect(endpoint);
        completion();
        return true;
    }

    disconnect(): void {
        this.sessionCreated = false;
        if (this.api.isConnected()) {
            this.api.disconnect();
        }
        this.conversation.clear();
    }

    isConnected(): boolean {
        return this.api.isConnected();
    }

    reset(): boolean {
        this.disconnect();
        this.clearEventHandlers();
        this.api.clearEventHandlers();
        this._resetConfig();
        this._addAPIEventHandlers();
        return true;
    }

    updateSession(sessionConfig: Partial<SessionConfig> = {}): boolean {
        Object.assign(this.sessionConfig, sessionConfig);
        if (this.api.isConnected()) {
            this.api.send('fabric.session.update', { session: this.sessionConfig });
        }
        return true;
    }

    deleteItem(id: string): boolean {
        this.api.send('fabric.conversation.item.delete', { item_id: id });
        return true;
    }

    send(eventName: string, data: Record<string, any> = {}): boolean {
        return this.api.send(eventName, data);
    }

    cancelResponse(id: string = ""): boolean {
        this.api.send('fabric.response.cancel');
        return true;
    }

    getItems(): any[] {
        return this.conversation.getItems();
    }

    async getInstances(): Promise<any> {
        return await this.api.get(`instances`, 60000) || [];
    }

    async getFiles(): Promise<any> {
        return await this.api.get(`files`, 30000) || [];
    }

    async uploadFile(file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);

        return await this.api.fetch('files/upload', 'POST', formData);
    }

    async replaceFile(fileId: string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);

        return await this.api.fetch(`files/${fileId}`, 'PUT', formData);
    }

    async downloadFile(fileId: string, fileName: string): Promise<void> {
        try {
            const response = await this.api.fetch(`files/${fileId}/download`, 'GET', undefined, 30000, true);

            // Create a clean filename from the user-friendly name
            const cleanFileName = fileName
                .toLowerCase()
                .replace(/\s+/g, '_') // Replace spaces with underscores
                .replace(/[^a-z0-9_]/g, '') // Remove any characters that aren't letters, numbers or underscores
                + '.yaml';

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', cleanFileName);

            document.body.appendChild(link);
            link.click();

            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
            throw error;
        }
    }

    async deleteFile(fileId: string): Promise<any> {
        return await this.api.fetch(`files/${fileId}`, 'DELETE');
    }

    async generateInstance(id: string, model_name: string, temperature: number): Promise<any> {
        return await this.api.post('instances/generate', {
            id: id,
            model_name: model_name,
            temperature: temperature
        });
    }

    async getUsername(): Promise<any> {
        if (this.api.username) {
            return this.api.username;
        }
        const username: { username: string } = await this.api.get(`username`, 5000);
        this.api.username = username['username'] || undefined
        return this.api.username
    }

    async getModels(): Promise<any> {
        if (this.api.models) {
            return this.api.models;
        }
        const models: {} = await this.api.get(`models`, 5000);
        this.api.models = models || {}
        return this.api.models
    }

    async getActivation(): Promise<any> {
        if (this.api.realtimeApiKey) {
            return this.api.realtimeApiKey;
        }
        const activation: { username: string, openai_api_key: string } = await this.api.get(`activate`, 5000);
        this.api.realtimeApiKey = activation?.openai_api_key || undefined
        return this.api.realtimeApiKey
    }

    async deleteInstance(id?: string): Promise<any> {
        const selection = id ? `/${id}` : ''
        const deletion: { status: string, deleted: number, total: number } = await this.api.get(`instances/delete${selection}`);
        return deletion
    }

    // Add event handler methods from RealtimeEventHandler
    on(eventName: string, handler: (...args: any[]) => void): void {
        super.on(eventName, handler);
    }

    // off(eventName: string, handler: (...args: any[]) => void): void {
    //     super.off(eventName, handler);
    // }

    // dispatch(eventName: string, ...args: any[]): void {
    //     super.dispatch(eventName, ...args);
    // }

    // clearEventHandlers(): void {
    //     super.clearEventHandlers();
    // }

    async waitForNext(eventName: string): Promise<any> {
        return super.waitForNext(eventName);
    }
}